import React, { useState } from 'react'
import { Skeleton, Stack, Typography, useTheme, Divider, Button } from '@mui/material'
import { useFetchActiveOrganization } from 'hooks/use-organization'
import { OrganizationFull, OrganizationKind, OrganizationUpdate } from 'api/model'
import { DatePicker } from '@mui/x-date-pickers'
import { orgsDeletePicture, orgsUpdate, orgsUploadPicture } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { SetterOrUpdater, useSetRecoilState } from 'recoil'
import { toastAtom } from 'state'
import CityAutocomplete from './CityAutocomplete'
import EditablePicture from 'components/EditablePicture'
import { isFieldEmpty, isWebAddressValid } from 'utils'
import IndustryPicker from './IndustryPicker'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { TextFieldWithDarkMode } from 'dark-mode-components'
import { getElementColor } from 'utils/darkMode'
import UserManagement from './UserManagement'
import { COLORS } from 'app-constants'

const Company = () => {
  const { organization, setOrganization, error } = useFetchActiveOrganization()
  const organizationKind = organization?.organization_kind

  const setToast = useSetRecoilState(toastAtom)

  const renderContent = () => {
    if (error) {
      return <div>{error}</div>
    }

    const renderForm = () => {
      if (!organization) {
        return <LoadingCompany />
      }

      return (
        <EditOrganization
          organization={organization}
          setOrganization={setOrganization}
          onFinishedEditing={(didUpdate) => {
            if (didUpdate) {
              setToast({
                title:
                  organizationKind === OrganizationKind.company
                    ? 'Company profile updated successfully'
                    : 'Personal space profile updated successfully',
                severity: 'success',
              })
            }
          }}
        />
      )
    }

    return <Stack direction="column">{renderForm()}</Stack>
  }

  return (
    <Stack
      height="100vh"
      sx={{
        p: 3,
        borderLeft: `1px solid ${COLORS.GRAY_6}`,
        backgroundColor: COLORS.PURE_WHITE,
      }}>
      {renderContent()}
    </Stack>
  )
}

const EditOrganization = ({
  organization,
  setOrganization,
  onFinishedEditing,
}: {
  organization: OrganizationFull
  setOrganization: SetterOrUpdater<OrganizationFull | null>
  onFinishedEditing: (didUpdate: boolean) => void
}) => {
  const theme = useTheme()

  const isMobile = useCheckMobileScreen()

  const setToast = useSetRecoilState(toastAtom)

  const [name, setName] = useState<string>(organization.name)
  const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
    setHasChanges(true)
  }

  const [description, setDescription] = useState<string>(organization.description ?? '')
  const onDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(event.target.value)
    setHasChanges(true)
  }

  const [website, setWebsite] = useState<string>(organization.website ?? '')
  const onWebsiteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWebsite(event.target.value)
    setHasChanges(true)
  }

  const [location, setLocation] = useState<string>(organization.location ?? '')
  const onLocationChange = (value: string | null) => {
    setLocation(value ?? '')
    setHasChanges(true)
  }

  const [industry, setIndustry] = useState<string>(organization.industry ?? '')
  const onIndustryChange = (value: string) => {
    setIndustry(value)
    setHasChanges(true)
  }

  const [year, setYear] = useState<Date | null>(() => {
    let currentDateYear: Date | null = null
    if (organization.founding_year) {
      currentDateYear = new Date()
      currentDateYear.setFullYear(organization.founding_year)
    }
    return currentDateYear
  })
  const onYearChange = (date: Date | null | undefined) => {
    if (!date) {
      setYear(null)
      setHasChanges(true)
      return
    }

    setYear(date)
    setHasChanges(true)
  }

  const [isSubmitting, setIsSubmitting] = useState(false)
  const onFormSubmit = async () => {
    if (!name) {
      return
    }

    try {
      setIsSubmitting(true)

      const update: OrganizationUpdate = {
        name,
        description,
        website,
        location,
        industry: industry,
        founding_year: year?.getFullYear(),
      }
      const result = await orgsUpdate(organization.id, update)
      setOrganization(result.data)
      onFinishedEditing(true)
    } catch (error) {
      onFinishedEditing(false)
      setToast({
        title: 'Company profile update failed',
        severity: 'error',
      })
    } finally {
      setIsSubmitting(false)
      setHasChanges(false)
    }
  }

  const [hasChanges, setHasChanges] = useState(false)

  const onPicturePicked: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
    if (!organization) {
      return
    }

    if (!event.target.files) {
      return
    }

    const file = event.target.files[0]

    try {
      const result = await orgsUploadPicture(organization.id, file, {
        headers: { 'Content-Type': 'application/octet-stream' },
      })

      setOrganization((organization) => {
        if (!organization) {
          return null
        }

        return {
          ...organization,
          picture: result.data.picture,
        }
      })
    } catch (Error) {
      setToast({ title: 'Failed to upload logo', severity: 'error' })
    }
  }

  const onPictureRemoved = async () => {
    if (!organization) {
      return
    }

    try {
      await orgsDeletePicture(organization.id)
      setOrganization((organization) => {
        if (!organization) {
          return null
        }

        const { picture, ...updatedOrganization } = organization
        return updatedOrganization
      })
    } catch (error) {
      setToast({ title: 'Logo removal failed', severity: 'error' })
    }
  }

  const renderPicture = () => {
    if (!organization) {
      return <Skeleton variant="rectangular" height={160} width={160} sx={{ marginTop: '8px' }} />
    }

    return (
      <EditablePicture
        onPicturePicked={onPicturePicked}
        onPictureRemoved={onPictureRemoved}
        src={organization.picture}
        changeLabel="logo"
        alt="Company profile"
      />
    )
  }

  return (
    <Stack mb={20} maxWidth="1024px">
      <Stack>
        {!isMobile && (
          <Typography variant="h4" width="120px">
            Company
          </Typography>
        )}
        <Stack spacing={4} sx={{ width: isMobile ? '100%' : '376px' }} pt={3}>
          {renderPicture()}
          <TextFieldWithDarkMode
            className="has-label"
            isDarkBackdrop={true}
            required
            theme={theme}
            label="Company name"
            placeholder="Enter name"
            value={name}
            onChange={onNameChange}
            disabled={isSubmitting}
            error={isFieldEmpty(name)}
            helperText={isFieldEmpty(name) && 'You must enter a company name'}
          />

          <TextFieldWithDarkMode
            className="has-label"
            theme={theme}
            isDarkBackdrop={true}
            multiline
            rows={3}
            label="Description"
            placeholder="Add here"
            value={description}
            onChange={onDescriptionChange}
            disabled={isSubmitting}
          />

          <TextFieldWithDarkMode
            className="has-label"
            isDarkBackdrop={true}
            theme={theme}
            label="Company website"
            placeholder="mycompany.com"
            value={website}
            onChange={onWebsiteChange}
            disabled={isSubmitting}
            error={!isWebAddressValid(website, true)}
            helperText={!isWebAddressValid(website, true) && 'You must enter a valid website'}
            sx={{
              '& .MuiFormLabel-root': {
                color: getElementColor(true, theme),
              },
            }}
          />

          <CityAutocomplete initialValue={location} onValueChanged={onLocationChange} disabled={isSubmitting} />

          <IndustryPicker value={industry} setIndustry={onIndustryChange} disabled={isSubmitting} />

          <DatePicker
            className="has-label"
            views={['year']}
            label="Founding year"
            value={year}
            onChange={onYearChange}
            disableFuture
            disabled={isSubmitting}
            renderInput={(params) => {
              const inputProps = Object.assign({}, params.inputProps, { placeholder: 'Add year' })
              const customParams = Object.assign({}, params, { inputProps })
              return (
                <TextFieldWithDarkMode
                  className="has-label"
                  isDarkBackdrop={false}
                  theme={theme}
                  {...customParams}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      marginRight: '12px',
                      fill: COLORS.GRAY_0,
                    },
                  }}
                />
              )
            }}
          />

          {hasChanges && (
            <Button
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              onClick={onFormSubmit}
              sx={{
                width: 'fit-content',
                alignSelf: 'flex-end',
              }}>
              Save changes
            </Button>
          )}
        </Stack>
      </Stack>

      <Divider
        sx={{
          my: 4,
        }}
      />

      <UserManagement />
    </Stack>
  )
}

const LoadingCompany = () => {
  const isMobile = useCheckMobileScreen()

  return (
    <Stack spacing={4} sx={{ width: isMobile ? '100%' : '376px' }}>
      <Skeleton variant="rectangular" height={56} />
      <Skeleton variant="rectangular" height={104} />
      <Skeleton variant="rectangular" height={56} />
      <Skeleton variant="rectangular" height={56} />
      <Skeleton variant="rectangular" height={56} />
      <Skeleton variant="rectangular" height={56} />
    </Stack>
  )
}

export default Company
